import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, exhaustMap, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { WorkCardAccessorService } from '../../widgets/staar/service-modules/data-accessors/work-card-accessor.service';
import { popToastError } from '../toast/toast';
import {
  fetchAttachments,
  fetchTechData,
  fetchWorkCardFiles,
  setAttachments,
  setTechData,
  setWorkCardFile,
  fetchParts,
  setParts,
  fetchTools,
  setTools,
  fetchWorkCard,
  addWorkCards,
  fetchSceptreDetails,
  setSceptreRequired,
  fetchLogPages,
  fetchAuditClosureReviews,
  setLogPages,
  setAuditQueue,
  setHoldPcQueue,
  noop,
  setAuditClosureReviews,
  setCustomerWorkCardQueue,
  fetchCIRSteps,
  setCIRSteps,
  fetchCircuitBreakers,
  setCircuitBreakers,
  fetchContinuationSheets,
  setContinuationSheets,
  fetchAllDocuments,
  setAllDocuments,
  patchWorkCard,
} from './workcards.actions';
import { Store } from '@ngrx/store';
import { selectedWorkCard } from '../selectors';

export const AUDIT_QUEUE_SESSION_STORAGE_KEY = 'auditQueueWorkCardIds';
export const HOLD_PC_QUEUE_SESSION_STORAGE_KEY = 'holdPcQueueWorkCardIds';
export const CUSTOMER_QUEUE_SESSION_STORAGE_KEY = 'customerQueueWorkCardIds';

@Injectable({
  providedIn: 'root',
})
export class WorkCardEffects {
 fetchWorkCard$ = createEffect(() =>
  this.actions$.pipe(
    ofType(fetchWorkCard.type),
    withLatestFrom(this.store.select(selectedWorkCard)),
    exhaustMap(([action, entities]: [any, any]) =>
      this.workCardAccessor.getWorkCardDetails(action.workCardId).pipe(
        map((workCardDetails) => {
          if (entities) {
            return patchWorkCard({
              workCardId: workCardDetails.WORK_CARD_ID,
              patch: workCardDetails
            });
          } else {
            return addWorkCards({ workCards: [workCardDetails] });
          }
        }),
        catchError((err) => of(popToastError({ toast: { message: err.message } })))
      )
    )
  )
);

  fetchAllWorkCardFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchWorkCardFiles.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getWorkCardPdf(action.workCardDetails).pipe(
          map((file) =>
            setWorkCardFile({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              file,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchWorkCardFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchWorkCardFiles.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getWorkCardPdf(action.workCardDetails).pipe(
          map((file) =>
            setWorkCardFile({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              file,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAttachments.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getAttachments(action.workCardDetails).pipe(
          map((attachments) =>
            setAttachments({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              attachments,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchAllDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAllDocuments.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getAllDocuments(action.workCardDetails).pipe(
          map((allDocuments) =>
            setAllDocuments({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              allDocuments,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchCIRSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCIRSteps.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getCIRSteps(action.workCardDetails).pipe(
          map((steps) =>
            setCIRSteps({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              steps,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchContinuationSheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchContinuationSheets.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getContinuationSheets(action.workCardDetails).pipe(
          map((continuationSheets) =>
              setContinuationSheets({
                workCardId: action.workCardDetails.WORK_CARD_ID,
                continuationSheets,
              })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchTechData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchTechData.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.getTechDataFiles(action.workCardDetails).pipe(
          map((techData) =>
            setTechData({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              techData,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchParts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchParts.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.fetchRequiredParts(action.workCardDetails).pipe(
          map((parts) =>
            setParts({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              parts,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchTools$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchTools.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.fetchTools(action.workCardDetails).pipe(
          map((tools) =>
            setTools({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              tools,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchSceptreDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSceptreDetails.type),
      exhaustMap((action: any) =>
        this.workCardAccessor
          .fetchCardClosureSceptreDetails(action.workCardDetails)
          .pipe(
            map((sceptreDetails) =>
              setSceptreRequired({
                workCardId: action.workCardDetails.WORK_CARD_ID,
                sceptreRequired: sceptreDetails.required,
              })
            ),
            catchError((err) =>
              of(popToastError({ toast: { message: err.message } }))
            )
          )
      )
    )
  );

  fetchLogPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchLogPages.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.fetchLogPages(action.workCardDetails).pipe(
          map((logPages) =>
            setLogPages({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              logPages,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  fetchAuditClosureReviews$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAuditClosureReviews.type),
      exhaustMap((action: any) =>
        this.workCardAccessor
          .fetchAuditClosureReviews(action.workCardDetails)
          .pipe(
            map((auditReviews) =>
              setAuditClosureReviews({
                workCardId: action.workCardDetails.WORK_CARD_ID,
                auditReviews,
              })
            ),
            catchError((err) =>
              of(popToastError({ toast: { message: err.message } }))
            )
          )
      )
    )
  );

  persistAuditQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setAuditQueue.type),
      tap((action: any) => {
        sessionStorage.setItem(
          AUDIT_QUEUE_SESSION_STORAGE_KEY,
          JSON.stringify(action.auditQueue)
        );
        sessionStorage.removeItem(HOLD_PC_QUEUE_SESSION_STORAGE_KEY);
      }),
      exhaustMap(() => of(noop()))
    )
  );

  persistHoldPcQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setHoldPcQueue.type),
      tap((action: any) => {
        sessionStorage.setItem(
          HOLD_PC_QUEUE_SESSION_STORAGE_KEY,
          JSON.stringify(action.holdPcQueue)
        );
        sessionStorage.removeItem(AUDIT_QUEUE_SESSION_STORAGE_KEY);
      }),
      exhaustMap(() => of(noop()))
    )
  );

  persistCustomerQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCustomerWorkCardQueue.type),
      tap((action: any) => {
        sessionStorage.setItem(
          CUSTOMER_QUEUE_SESSION_STORAGE_KEY,
          JSON.stringify(action.queue)
        );
      }),
      exhaustMap(() => of(noop()))
    )
  );

  fetchCircuitBreakers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCircuitBreakers.type),
      exhaustMap((action: any) =>
        this.workCardAccessor.fetchCircuitBreakers(action.workCardDetails).pipe(
          map((circuitBreakers) =>
            setCircuitBreakers({
              workCardId: action.workCardDetails.WORK_CARD_ID,
              circuitBreakers,
            })
          ),
          catchError((err) =>
            of(popToastError({ toast: { message: err.message } }))
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly workCardAccessor: WorkCardAccessorService,
    private readonly store: Store
  ) {}
}
